import React, { useState } from "react";
import { DateRange } from "react-date-range";
import {
  subDays,
  subYears,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  format,
  differenceInDays,
  isSameMonth,
} from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const predefinedRanges = [
  {
    label: "Today",
    range: () => ({
      startDate: new Date(),
      endDate: new Date(),
    }),
    visible: true,
  },
  {
    label: "Yesterday",
    range: () => ({
      startDate: subDays(new Date(), 1),
      endDate: subDays(new Date(), 1),
    }),
    visible: true,
  },
  {
    label: "This Week",
    range: () => ({
      startDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
      endDate: new Date(),
    }),
    visible: true,
  },
  {
    label: "Last Week",
    range: () => ({
      startDate: subDays(startOfWeek(new Date(), { weekStartsOn: 0 }), 7),
      endDate: subDays(startOfWeek(new Date(), { weekStartsOn: 0 }), 1),
    }),
    visible: true,
  },
  {
    label: "Last 7 days",
    range: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }),
    visible: true,
  },
  {
    label: "Last 14 days",
    range: () => ({ startDate: subDays(new Date(), 13), endDate: new Date() }),
    visible: true,
  },
  {
    label: "Last 30 days",
    range: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }),
    visible: true,
  },
  {
    label: "This Month",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
  {
    label: "Last Month",
    range: () => {
      const prevMonth = subDays(startOfMonth(new Date()), 1);
      return {
        startDate: startOfMonth(prevMonth),
        endDate: endOfMonth(prevMonth),
      };
    },
    visible: true,
  },
  {
    label: "All Time",
    range: () => {
      const startDate = localStorage.getItem("start_date")
        ? new Date(localStorage.getItem("start_date"))
        : new Date(localStorage.getItem("2020-01-01"));
      return {
        startDate: startDate,
        endDate: new Date(),
      };
    },
    visible: true,
  },
];
// Helpers
const computePreviousRange = (start, end) => {
  const diff = differenceInDays(end, start) + 1;
  return {
    startDate: subDays(start, diff),
    endDate: subDays(end, diff),
    key: "compare",
    color: "#34a853",
  };
};

const computePreviousYearRange = (start, end) => ({
  startDate: subYears(start, 1),
  endDate: subYears(end, 1),
  key: "compare",
  color: "#34a853",
});

const CalendarWithCompare = ({
  initialPrimaryRange,
  initialCompareRange,
  onApply,
  onClose,
  activePreset,
  setActivePreset,
  isCompare,
  setIsCompare,
}) => {
  const [primaryRange, setPrimaryRange] = useState({
    ...initialPrimaryRange,
    key: "primary",
    className: "primary-range",
  });

  const defaultCompare = computePreviousRange(
    initialPrimaryRange.startDate,
    initialPrimaryRange.endDate,
  );

  const [compareType, setCompareType] = useState(
    initialCompareRange ? "custom" : "previous_period",
  );

  const [compareRange, setCompareRange] = useState(
    initialCompareRange
      ? { ...initialCompareRange, key: "compare", className: "compare-range" }
      : { ...defaultCompare, className: "compare-range" },
  );
  const updateCompareAutomatically = (start, end, type) => {
    let newRange;

    if (type === "previous_period") {
      newRange = computePreviousRange(start, end);
    } else if (type === "previous_year") {
      newRange = computePreviousYearRange(start, end);
    }

    setCompareRange({
      ...newRange,
      key: "compare",
      className: "compare-range",
    });
  };

  // --- START OF FIX ---
  const handlePresetClick = (label, rangeFn) => {
    setActivePreset(label);
    const newRange = rangeFn();

    setPrimaryRange({
      ...newRange,
      key: "primary",
      className: "primary-range",
    });

    if (label === "All Time") {
      setIsCompare(false);
    } else {
      if (isCompare && compareType !== "custom") {
        updateCompareAutomatically(
          newRange.startDate,
          newRange.endDate,
          compareType,
        );
      }
    }
  };
  // --- END OF FIX ---

  const handleRangeSelect = (ranges) => {
    const primary = ranges.primary;
    const compare = ranges.compare;

    if (primary) {
      const { startDate, endDate } = primary;

      const isSingleDay =
        startDate && endDate && startDate.getTime() === endDate.getTime();

      const finalRange = {
        startDate,
        endDate,
      };

      setPrimaryRange({
        ...finalRange,
        key: "primary",
        className: "primary-range",
      });

      // Manual selection always switches to Custom
      setActivePreset("Custom");

      // Auto-update compare only when NOT custom
      if (isCompare && compareType !== "custom") {
        updateCompareAutomatically(startDate, endDate, compareType);
      }
    }

    if (compare) {
      setIsCompare(true);
      setCompareType("custom");

      setCompareRange({
        ...compare,
        key: "compare",
        className: "compare-range",
      });

      setActivePreset("Custom");
    }
  };

  const handleApply = () => {
    onApply(primaryRange, isCompare ? compareRange : null, isCompare);
  };
  const handleCompareToggle = (e) => {
    const checked = e.target.checked;
    setIsCompare(checked);

    if (checked && compareType !== "custom") {
      updateCompareAutomatically(
        primaryRange.startDate,
        primaryRange.endDate,
        compareType,
      );
    }
  };

  const handleCompareSelect = (e) => {
    const type = e.target.value;
    setCompareType(type);

    if (type !== "custom") {
      updateCompareAutomatically(
        primaryRange.startDate,
        primaryRange.endDate,
        type,
      );
    }
  };

  const compareDisabled = () => {
    return activePreset === "All Time";
  };

  return (
    <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-1 w-[680px] mx-auto z-40 flex flex-col font-sans">
      <div className="flex flex-row">
        
        {/* SIDEBAR PRESETS */}
        <div className="w-1/3 border-r border-gray-200 p-3 flex flex-col justify-between bg-white">
          <div className="flex flex-col space-y-1">
            <button
              onClick={() => setActivePreset("Custom")}
              className={`text-left px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                activePreset === "Custom"
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              Custom
            </button>
            {predefinedRanges.map(({ label, range }) => (
              <button
                key={label}
                onClick={() => handlePresetClick(label, range)}
                className={`text-left px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  activePreset === label
                    ? "bg-blue-50 text-blue-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* COMPARE TOGGLE */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between px-2">
            <span className={`text-sm font-medium ${compareDisabled() ? "text-gray-400" : "text-gray-700"}`}>
              Compare
            </span>
            <label className={`relative inline-flex items-center ${compareDisabled() ? "cursor-not-allowed" : "cursor-pointer"}`}>
              <input
                type="checkbox"
                checked={isCompare}
                onChange={handleCompareToggle}
                className="sr-only peer"
                disabled={compareDisabled()}
              />
              <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 
                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 
                after:transition-all peer-checked:after:translate-x-4 peer-checked:after:border-white">
              </div>
            </label>
          </div>
        </div>

        {/* CALENDAR AREA */}
        <div className="w-2/3 p-4 flex flex-col">
          
          {/* HEADER INPUTS */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 bg-white border border-gray-300 rounded-md shadow-sm px-3 py-2 text-center text-sm font-medium text-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
              {format(primaryRange.startDate, "MMM d, yyyy")}
            </div>
            <span className="text-gray-400 font-medium">—</span>
            <div className="flex-1 bg-white border border-gray-300 rounded-md shadow-sm px-3 py-2 text-center text-sm font-medium text-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
              {format(primaryRange.endDate, "MMM d, yyyy")}
            </div>
          </div>

          {/* RENDER DATE RANGE */}
          <div className={`flex-1 ${isCompare ? "google-ads-calendar compare-mode" : "google-ads-calendar"}`}>
            <DateRange
              onChange={handleRangeSelect}
              ranges={isCompare ? [primaryRange, compareRange] : [primaryRange]}
              months={1}
              direction="vertical"
              scroll={{ enabled: true }}
              showDateDisplay={false}
              moveRangeOnFirstSelection={false}
              showMonthAndYearPickers={true}
              maxDate={new Date()}
            />
          </div>
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="flex justify-end px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          className="ml-3 px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm transition-colors"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export const handleApplyDateRanges = (
  primary,
  compare,
  isCompareEnabled,
  setState,
  closeDatePicker,
  setRandom,
) => {
  let newState = [
    {
      startDate: primary.startDate,
      endDate: primary.endDate,
      key: "selection",
    },
  ];

  if (isCompareEnabled && compare) {
    newState.push({
      startDate: compare.startDate,
      endDate: compare.endDate,
      key: "compare",
    });
  }

  setState(newState);
  closeDatePicker();

  if (setRandom) {
    setRandom(Math.random());
  }
};

export const DateRangeSelector = ({
  state,
  setState,
  setRandom,
  showDatePicker,
  toggleDropDownTabs,
  datePickerRef,
  Page,
}) => {
  const [activePreset, setActivePreset] = useState("Custom");
  const [isCompare, setIsCompare] = useState(state.length > 1);

  const formatButtonLabel = () => {
    const { startDate, endDate } = state[0];

    const sameYear = format(startDate, "yyyy") === format(endDate, "yyyy");

    let startFormat = "MMM d";

    if (!sameYear) {
      startFormat = "MMM d, yyyy";
    }

    const startPart = format(startDate, startFormat);
    const endPart = format(endDate, "MMM d, yyyy");

    return `${startPart} – ${endPart}`;
  };

  const formatCompareLabel = () => {
    if (state.length < 2) return "";

    const { startDate, endDate } = state[1];
    const sameYear = format(startDate, "yyyy") === format(endDate, "yyyy");

    let startFormat = "MMM d";

    if (!sameYear) {
      startFormat = "MMM d, yyyy";
    }

    const startPart = format(startDate, startFormat);
    const endPart = format(endDate, "MMM d, yyyy");

    return `Compared: ${startPart} – ${endPart}`;
  };

  const handleLast30DaysClick = () => {
    // Find the "Last 30 days" preset configuration
    const last30DaysPreset = predefinedRanges.find(
      (p) => p.label === "Last 30 days",
    );
    if (!last30DaysPreset) return; // Safeguard

    // Calculate the new primary date range
    const primary = last30DaysPreset.range();

    // Set the active preset label for visual feedback
    setActivePreset("Last 30 days");

    // Re-use the existing apply logic to update the state
    handleApplyDateRanges(
      primary,
      null, // No compare range when clicking this shortcut
      false, // Explicitly set compare to false
      setState,
      () => toggleDropDownTabs("Hide all"), // Close datepicker if open
      setRandom,
    );
  };

  return (
    <div className="flex justify-between items-center my-5">
      <h2 className="text-2xl font-semibold text-gray-800">{Page}</h2>

      <div className="flex items-center space-x-4">
        {/* Calendar Comp */}
        <div className="relative" ref={datePickerRef}>
          <div className="flex flex-col text-center">
            <div className="flex items-center space-x-3">
              <span className="text-gray-600 font-medium">{activePreset}</span>
              <button
                onClick={() => toggleDropDownTabs("Calendar")}
                className="border border-gray-400 bg-white rounded-md px-4 py-2 text-sm 
                       hover:border-blue-500 hover:bg-blue-50 transition w-64"
              >
                {formatButtonLabel()}
              </button>
            </div>
            {isCompare && (
              <p className="text-xs text-gray-500 mt-1 text-right pr-2">
                {formatCompareLabel()}
              </p>
            )}
          </div>
          {showDatePicker && (
            <div className="absolute right-0 mt-2 z-40 bg-white shadow-xl rounded-xl border border-gray-200">
              <CalendarWithCompare
                initialPrimaryRange={{
                  startDate: state[0].startDate,
                  endDate: state[0].endDate,
                  key: "primary",
                }}
                initialCompareRange={
                  state.length > 1
                    ? {
                        startDate: state[1].startDate,
                        endDate: state[1].endDate,
                        key: "compare",
                      }
                    : null
                }
                onApply={(primary, compare, isCompareEnabled) =>
                  handleApplyDateRanges(
                    primary,
                    compare,
                    isCompareEnabled,
                    setState,
                    () => toggleDropDownTabs("Hide all"),
                    setRandom,
                  )
                }
                onClose={() => toggleDropDownTabs("Hide all")}
                activePreset={activePreset}
                setActivePreset={setActivePreset}
                isCompare={isCompare}
                setIsCompare={setIsCompare}
              />
            </div>
          )}
        </div>
        <div
          className="text-blue-500 cursor-pointer font-semibold text-sm"
          onClick={handleLast30DaysClick} // Attached the new handler here
        >
          Show last 30 days
        </div>
      </div>
    </div>
  );
};
export default CalendarWithCompare;
